import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Inbox, UserPlus } from 'lucide-react'
import { settings as settingsApi } from '@/api/endpoints'
import type { ReviewItem, ReviewKind } from '@/api/types'
import { Err } from '@/components/Err'
import { Empty, PageHead, Section, SectionCrumb } from '@/components/layout/Page'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { KIND_ORDER, KIND_TITLE, type BatchAction, batchable, reviewKey } from '@/domain/review'
import type { World } from './model'
import { pill } from './pills'
import { BatchActions, type BatchFailure } from './review/batch'
import { ApprovalRow, ChangeRow, DecisionRow, NoticeRow, OfflineNodeRow, SignupRow, type RowProps } from './review/rows'

// 로그인 직후의 첫 화면 — 관리자가 처리해야 할 것. 서버가 조합한 항목을 종류별 절로 놓고, 모든 조작은 이 화면 안에서 끝난다.
// 끝난 항목은 다음 읽기가 오기 전까지 이 화면이 스스로 숨긴다(done). 종류의 순서와 항목의 순서는 서버의 것이다.
// 제목 오른쪽의 종류 알약은 절을 고르고, 제목 줄 오른쪽의 칩은 체크한 항목을 한 번에 끝낸다(`review/batch.tsx`).
// 고른 항목은 보이는 절의 것만 대상이다 — 보이지 않는 줄에 조작하지 않는다.
// 일괄 조작이 도는 동안은 고르기(체크박스·절 전체 선택·종류 알약)와 고른 줄의 칩이 잠긴다 — 누른 순간의 목록을 끝까지 돈다.
// 가입 신청은 가입 관리(`/signups`, F-121)가 같은 장치로 처리한다 — 여기에는 건수와 그 화면으로 가는 링크만 남는다.

const ROW: Partial<Record<ReviewKind, (props: RowProps) => React.JSX.Element | null>> = {
  signup_request: SignupRow,
  gpu_approval: ApprovalRow,
  lease_change: ChangeRow,
  cluster_decision: DecisionRow,
  node_offline: OfflineNodeRow,
  notice: NoticeRow,
}

/** scope 가 signups 면 가입 관리 화면이다 — 가입 신청만 보이고 머리가 다르다(F-121). */
export default function Review({ world, loading, scope = 'review' }: { world: World; loading: boolean; scope?: 'review' | 'signups' }) {
  const [done, setDone] = useState<ReadonlySet<string>>(new Set())
  const [kind, setKind] = useState<ReviewKind | null>(null)
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set())
  const [busy, setBusy] = useState<BatchAction | null>(null)
  const [failure, setFailure] = useState<BatchFailure | null>(null)
  const inScope = (item: ReviewItem) => (scope === 'signups') === (item.kind === 'signup_request')
  const items = world.review.filter((item) => ROW[item.kind] && inScope(item) && !done.has(reviewKey(item)))
  // 검토 대기가 가입 관리로 넘기는 것 — 처리하지 않으므로 건수만 센다.
  const signups = scope === 'review' ? world.review.filter((item) => item.kind === 'signup_request').length : 0
  const finish = (item: ReviewItem) => setDone((prev) => new Set(prev).add(reviewKey(item)))

  // 고른 종류가 다 끝나 사라지면 전체로 돌아간다 — 빈 절만 남은 화면은 할 일이 없다고 잘못 말한다.
  // 상태 자체를 되돌린다: 같은 종류가 다시 들어와도 누르지 않은 거르개가 저절로 서지 않게.
  const present = KIND_ORDER.filter((k) => items.some((item) => item.kind === k))
  if (kind !== null && !present.includes(kind)) setKind(null)
  const only = kind !== null && present.includes(kind) ? kind : null
  const shown = only ? items.filter((item) => item.kind === only) : items
  const chosen = shown.filter((item) => batchable(item) && picked.has(reviewKey(item)))
  // 일괄 승인·무시의 오류는 실패한 항목이 골라져 있는 동안만 선다 — 그 줄이 끝났거나(다른 곳에서 결정됐다)
  // 고르기를 풀었으면 다시 시도할 칩도 없다.
  if (failure !== null && !chosen.some((item) => failure.keys.has(reviewKey(item)))) setFailure(null)
  const locked = busy !== null
  const pick = (keys: string[], on: boolean) =>
    setPicked((prev) => {
      const next = new Set(prev)
      for (const key of keys) {
        if (on) next.add(key)
        else next.delete(key)
      }
      return next
    })

  const nothing = scope === 'signups' ? '가입 신청 없음' : '검토할 것 없음'
  const count = loading ? '불러오는 중' : items.length === 0 ? nothing : chosen.length > 0 ? `${items.length}건 · ${chosen.length}건 선택` : `${items.length}건`

  return (
    <>
      <SectionCrumb />
      <PageHead
        icon={scope === 'signups' ? <UserPlus className="size-5" /> : <Inbox className="size-5" />}
        title={scope === 'signups' ? '가입 관리' : '검토 대기'}
        afterTitle={
          // 종류가 하나뿐이면 좁힐 것이 없다.
          present.length > 1 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <button type="button" disabled={locked} onClick={() => setKind(null)} aria-pressed={only === null} className={pill(only === null)}>
                전체 <span className="tabular-nums">{items.length}</span>
              </button>
              {present.map((k) => (
                <button key={k} type="button" disabled={locked} onClick={() => setKind(k)} aria-pressed={only === k} className={pill(only === k)}>
                  {KIND_TITLE[k]} <span className="tabular-nums">{items.filter((item) => item.kind === k).length}</span>
                </button>
              ))}
            </div>
          ) : undefined
        }
        facts={count}
        right={<BatchActions chosen={chosen} busy={busy} onBusy={setBusy} onDone={finish} onFailure={setFailure} />}
      />
      {failure ? (
        <div className="pt-3">
          <Err error={failure.error} />
        </div>
      ) : null}
      {scope === 'signups' ? <SignupSwitch /> : null}
      {signups > 0 ? (
        <Section title={`가입 신청 ${signups}`}>
          <Link to="/signups" className="text-[13px] font-medium text-brand hover:underline">가입 관리</Link>
        </Section>
      ) : null}
      {items.length === 0 && signups === 0 && !loading ? <Empty>{nothing}</Empty> : null}
      {KIND_ORDER.map((k) => {
        const rows = shown.filter((item) => item.kind === k)
        const Component = ROW[k]
        if (rows.length === 0 || !Component) return null
        const keys = rows.filter(batchable).map(reviewKey)
        const all = keys.length > 0 && keys.every((key) => picked.has(key))
        return (
          <Section
            key={k}
            title={`${KIND_TITLE[k]} ${rows.length}`}
            lead={keys.length > 0 ? <Checkbox aria-label={`${KIND_TITLE[k]} 전체 선택`} checked={all} disabled={locked} onCheckedChange={(on) => pick(keys, on === true)} /> : undefined}
          >
            <ul className="divide-y overflow-hidden rounded-lg border bg-card">
              {rows.map((item) => {
                const key = reviewKey(item)
                return (
                  <Component
                    key={key}
                    item={item}
                    onDone={() => finish(item)}
                    pick={batchable(item) ? { checked: picked.has(key), locked, onChange: (on) => pick([key], on) } : undefined}
                  />
                )
              })}
            </ul>
          </Section>
        )
      })}
    </>
  )
}

// 회원가입을 받을지(F-76). 끄면 로그인 화면의 `계정 신청` 이 사라진다 — 그 결과는 로그인 화면이 말한다.
// 스위치가 곧 저장이다 — 값이 하나뿐이라 따로 누를 버튼이 없다.
function SignupSwitch() {
  const [on, setOn] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  useEffect(() => {
    let live = true
    settingsApi
      .get()
      .then((s) => live && setOn(s.signup_enabled))
      .catch((err: unknown) => live && setError(err))
    return () => {
      live = false
    }
  }, [])
  const flip = async (next: boolean) => {
    setBusy(true)
    setError(null)
    try {
      setOn((await settingsApi.put({ signup_enabled: next })).signup_enabled)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Section title="회원가입">
      <div className="flex items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3">
        <Label htmlFor="settings-signup">회원가입 받기</Label>
        <Switch id="settings-signup" checked={on ?? false} disabled={busy || on === null} onCheckedChange={(v) => void flip(v)} />
      </div>
      <Err error={error} />
    </Section>
  )
}
